import React from 'react'
import { View, StyleSheet, Platform } from 'react-native'
import { Button } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-native-phone-input'

/**
 * The signup funnel's phone field.
 *
 * ─── WHY THIS IS ITS OWN COMPONENT (WA-ONBOARD-001) ──────────────────────────────────────────
 * `PhoneInput` is UNCONTROLLED: it keeps the number in its own state and hands it out through an
 * imperative `getValue()`. For years the only two places that called it were this field's own
 * "continue" button and the keyboard's return key — so the value reaching the form and the button
 * being pressed were, without anybody deciding it, the same event.
 *
 * That coupling is invisible until something else owns the button. Adding the WhatsApp opt-in
 * below this field did exactly that (a consent has to sit above the action that commits it), and
 * the result was a signup funnel that dead-ended at the phone screen on every platform: `data.phone`
 * was `undefined`, the field is `required`, and the screen simply refused to advance. Nothing threw
 * and nothing logged.
 *
 * So the value is harvested on CHANGE and the button is just a button.
 *
 * ⚠️ `onChangePhoneNumber` is called with the DISPLAY value (`+7 (700) 123-45-67`); `getValue()`
 * returns the raw one (`+77001234567`). We report the raw one, because that is what signup has
 * always written to `users.phone` on both sides — taking the argument would quietly re-format every
 * new organization's stored number.
 */
export default function PhoneField ({ inputRef, onChange, onSubmit, showContinueButton }) {
  const { t } = useTranslation()

  const harvest = () => {
    // The handle is null until the widget mounts, and a programmatic setValue can fire the change
    // before then. Reporting `undefined` would clear a number the merchant has already typed.
    const value = inputRef.current?.getValue()
    if (value !== undefined && value !== null) onChange(value)
  }

  return (
    <View>
      <PhoneInput
        ref={inputRef}
        autoFormat={true}
        initialCountry='kz'
        style={styles.phoneFieldStyle}
        textStyle={{ height: 50, paddingRight: 20 }}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
        onPressFlag={Platform.OS === 'web' ? () => {} : null}
        allowZeroAfterCountryCode={true}
        onChangePhoneNumber={harvest}
        // Picking a country RESETS the widget's value to that country's bare dial code, and it does
        // NOT call `onChangePhoneNumber`. While this field owned the continue button that did not
        // matter — the button re-read the widget on the way out. It no longer owns it, so without
        // this the form keeps the number typed under the OLD flag: the funnel advances with a number
        // that is not the one on screen, and it is the number the WhatsApp consent is attached to.
        onSelectCountry={harvest}
        onSubmitEditing={() => {
          harvest()
          onSubmit()
        }}
        autoFocus={true}
      />
      {showContinueButton && (
        <Button
          onPress={() => {
            // Harvested again rather than trusted: the change handler is the primary path, and a
            // press that does not first re-read the widget is how this field lost its value before.
            harvest()
            onSubmit()
          }}
        >
          {t('continue')}
        </Button>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  phoneFieldStyle: {
    height: 50,
    backgroundColor: '#f7f9fc',
    borderRadius: 4,
    borderColor: '#e4e9f2',
    borderWidth: 1,
    paddingLeft: 20,
    textAlignVertical: 'center',
    marginBottom: 20
  }
})
